import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUser } from 'react-icons/fi';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { PERMISSIONS, buildRoleFeatureMatrix, canFeatureForRole } from '../../utils/permissions';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import Pagination from '../../components/common/Pagination';
import {
  ABANDONED_FEEDBACK_MARKER,
  isAbandonMarkerScoreValue,
  isAbandonedFeedbackEntry,
} from '../../utils/matchFeedbackAbandoned';
import {
  formatMatchDayPillLabel,
  formatScheduledStartLabel,
  isTlFeedbackWindowOpen,
} from '../../utils/matchFeedbackSchedule';
import { FEEDBACK_TABLE_HEAD_GRADIENT } from '../../utils/feedbackViewSharedUi';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import { isDaysMatchType } from '../../utils/matchTypeUi';
import { getRoleIconComponent, TEAM_MEMBER_MODAL_ACCENT } from '../../utils/teamMemberDetailModalUi';

const normalizeJobType = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\s/_-]/g, '');

const toScoreKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/[^a-z0-9]/g, '');

const normalizeScoreKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const normalizeRatingValue = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.min(5, Math.round(parsed)));
};

const extractFeedbackPayload = (response) =>
  response?.data?.data || response?.data || response || null;

const resolveEvaluatorName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return (
      value.name ||
      value.fullName ||
      value.userName ||
      value.username ||
      value.email ||
      ''
    );
  }
  return '';
};

const resolveLabelText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return (
      value.matchLocation ||
      value.venue ||
      value.location ||
      value.name ||
      value.teamName ||
      value.eventName ||
      value.title ||
      value.label ||
      value.value ||
      ''
    );
  }
  return '';
};

const extractFeedbackEntries = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.feedback)) return payload.feedback;
  if (Array.isArray(payload.entries)) return payload.entries;
  if (Array.isArray(payload.ratings)) return payload.ratings;
  return [];
};

const getParameterNamesFromScores = (scores) => {
  if (!scores || typeof scores !== 'object' || Array.isArray(scores)) return [];
  const byNormalized = new Map();
  Object.keys(scores).forEach((rawName) => {
    const name = String(rawName || '').trim();
    if (!name) return;
    const normalized = normalizeScoreKey(name);
    if (!normalized) return;
    const existing = byNormalized.get(normalized);
    if (!existing) {
      byNormalized.set(normalized, name);
      return;
    }
    const nextLooksReadable = /[\s_-]/.test(name);
    const existingLooksReadable = /[\s_-]/.test(existing);
    if (nextLooksReadable && !existingLooksReadable) {
      byNormalized.set(normalized, name);
      return;
    }
    if (name.length > existing.length) {
      byNormalized.set(normalized, name);
    }
  });
  return Array.from(byNormalized.values());
};

const coerceResolvedScore = (raw, defaultValue) => {
  if (typeof raw === 'string') {
    const u = raw.trim().toUpperCase();
    if (u === 'AB' || u === 'N/A') return ABANDONED_FEEDBACK_MARKER;
  }
  return normalizeRatingValue(raw, defaultValue);
};

const resolveScoreValue = (sourceScores, parameterName, defaultValue = 1) => {
  if (!sourceScores || typeof sourceScores !== 'object') return normalizeRatingValue(defaultValue, 1);

  const keyByLabel = parameterName;
  const keyByCamel = toScoreKey(parameterName);
  const normalizedTarget = normalizeScoreKey(parameterName);

  if (sourceScores[keyByLabel] !== undefined) {
    return coerceResolvedScore(sourceScores[keyByLabel], defaultValue);
  }
  if (sourceScores[keyByCamel] !== undefined) {
    return coerceResolvedScore(sourceScores[keyByCamel], defaultValue);
  }

  const matchedKey = Object.keys(sourceScores).find(
    (key) => normalizeScoreKey(key) === normalizedTarget
  );
  if (matchedKey) return coerceResolvedScore(sourceScores[matchedKey], defaultValue);

  return normalizeRatingValue(defaultValue, 1);
};

const formatViewScoreValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const u = trimmed.toUpperCase();
    if (u === 'AB' || u === 'N/A') return ABANDONED_FEEDBACK_MARKER;
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber)) return normalizeRatingValue(asNumber, 1);
    return trimmed;
  }
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return normalizeRatingValue(asNumber, 1);
  return String(value);
};

const formatParameterHeader = (value) =>
  String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const toStableRowId = (value, fallback) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  if (value && typeof value === 'object') {
    const nested =
      value._id ||
      value.id ||
      value.employeeId ||
      value.userId ||
      value.memberId ||
      value.value;
    if (nested !== undefined && nested !== null) {
      const normalizedNested = String(nested).trim();
      if (normalizedNested) return normalizedNested;
    }
  }
  return fallback;
};

const resolveTeamMemberName = (member) => {
  if (!member || typeof member !== 'object') return '';
  return (
    member?.member?.name ||
    member?.member?.fullName ||
    member?.employee?.name ||
    member?.employee?.fullName ||
    member?.user?.name ||
    member?.user?.fullName ||
    member?.name ||
    member?.fullName ||
    member?.employeeName ||
    ''
  );
};

const resolveTeamMemberId = (member) => {
  if (!member || typeof member !== 'object') return '';
  const value =
    member?.member?._id ||
    member?.member?.id ||
    member?.member ||
    member?.employeeId ||
    member?.userId ||
    member?.memberId ||
    member?.employee?._id ||
    member?.employee?.id ||
    member?.id ||
    member?._id;
  return value !== undefined && value !== null ? String(value).trim() : '';
};

const resolveTeamMemberRole = (member) => {
  if (!member || typeof member !== 'object') return '';
  const roleValue =
    member?.role?.name ||
    member?.role?.title ||
    member?.role?.jobType ||
    member?.role?.role ||
    member?.role ||
    member?.member?.jobType?.name ||
    member?.member?.jobType?.title ||
    member?.member?.jobType?.jobType ||
    member?.member?.jobType ||
    member?.employee?.jobType?.name ||
    member?.employee?.jobType ||
    member?.user?.jobType?.name ||
    member?.user?.jobType ||
    member?.jobTypeName ||
    member?.jobType ||
    member?.designation ||
    '';
  return String(roleValue || '').trim();
};

const resolveTeamMemberRoleId = (member) => {
  if (!member || typeof member !== 'object') return '';
  const roleId =
    member?.role ||
    member?.member?.jobType?._id ||
    member?.member?.jobType?.id ||
    member?.employee?.jobType?._id ||
    member?.employee?.jobType?.id ||
    member?.user?.jobType?._id ||
    member?.user?.jobType?.id ||
    member?.role?._id ||
    member?.role?.id ||
    member?.roleId ||
    member?.jobTypeId;
  return roleId !== undefined && roleId !== null ? String(roleId).trim() : '';
};

const resolveEntryEmployeeId = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const value =
    entry?.employeeId ||
    entry?.userId ||
    entry?.memberId ||
    entry?.employee?._id ||
    entry?.employee?.id ||
    entry?.user?._id ||
    entry?.user?.id ||
    entry?.member?._id ||
    entry?.member?.id;
  if (value && typeof value === 'object') {
    const nested = value?._id || value?.id || value?.value;
    return nested !== undefined && nested !== null ? String(nested).trim() : '';
  }
  return value !== undefined && value !== null ? String(value).trim() : '';
};

const resolveEntryEmployeeName = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const value =
    entry?.employeeName ||
    entry?.employeeId?.name ||
    entry?.employeeId?.fullName ||
    entry?.employee?.name ||
    entry?.employee?.fullName ||
    entry?.member?.name ||
    entry?.member?.fullName ||
    entry?.user?.name ||
    entry?.user?.fullName ||
    entry?.name ||
    '';
  return String(value || '').trim();
};

const resolveEntryRoleLabel = (entry, fallbackLabel) =>
  String(
    entry?.role?.name ||
      entry?.role?.title ||
      entry?.role?.jobType ||
      entry?.role?._id ||
      entry?.role?.id ||
      entry?.jobType?.name ||
      entry?.jobType?.title ||
      entry?.jobType?.jobType ||
      entry?.jobType?._id ||
      entry?.jobType?.id ||
      entry?.role ||
      entry?.jobType ||
      entry?.jobTypeName ||
      entry?.roleName ||
      entry?.eventRole?.name ||
      entry?.eventRole?._id ||
      entry?.eventRole?.id ||
      entry?.eventRole ||
      entry?.eventRoleName ||
      fallbackLabel ||
      ''
  ).trim();

const getDaysDuration = (startDateValue, endDateValue) => {
  const start = new Date(startDateValue);
  const end = new Date(endDateValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const oneDayMs = 24 * 60 * 60 * 1000;
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const diffDays = Math.floor((utcEnd - utcStart) / oneDayMs) + 1;
  if (!Number.isFinite(diffDays) || diffDays <= 0) return 1;
  return Math.max(1, diffDays);
};

const resolveMatchTypeForEventFeedback = (eventTeam, navState) =>
  navState?.matchType ??
  eventTeam?.matchId?.matchType ??
  eventTeam?.match?.matchType ??
  eventTeam?.matchType ??
  '';

const extractSubmittedDaysForEventTeam = (responseData, eventTeamId) => {
  const payload = responseData?.data || responseData;
  const targetEventTeamId = String(eventTeamId || '').trim();
  const days = new Set();
  const collectFromRecord = (record) => {
    if (!record || typeof record !== 'object') return;
    const recordEventTeamId = String(
      record?.eventTeamId?._id || record?.eventTeamId?.id || record?.eventTeamId || ''
    ).trim();
    if (targetEventTeamId && recordEventTeamId && recordEventTeamId !== targetEventTeamId) return;
    const rawDay = record?.feedbackDay ?? record?.matchDay ?? record?.dayNumber ?? null;
    const day = Number(rawDay);
    if (Number.isFinite(day) && day >= 1) days.add(day);
  };

  const candidateLists = [];
  if (Array.isArray(payload)) candidateLists.push(payload);
  if (Array.isArray(payload?.feedbacks)) candidateLists.push(payload.feedbacks);
  if (Array.isArray(payload?.records)) candidateLists.push(payload.records);
  if (Array.isArray(payload?.data)) candidateLists.push(payload.data);
  candidateLists.forEach((list) => list.forEach(collectFromRecord));
  collectFromRecord(payload);
  return Array.from(days).sort((a, b) => a - b);
};

/** Mongo may store scores as a map or as [{ parameterName, score }, ...]. */
const normalizeScoresShape = (scores) => {
  if (scores == null) return {};
  if (Array.isArray(scores)) {
    return scores.reduce((acc, item) => {
      if (!item || typeof item !== 'object') return acc;
      const name =
        item.parameterName ||
        item.name ||
        item.parameter ||
        item.title ||
        item.key;
      const val =
        item.score ??
        item.value ??
        item.rating ??
        item.mark;
      if (name != null && String(name).trim()) {
        acc[String(name).trim()] = val;
      }
      return acc;
    }, {});
  }
  if (typeof scores === 'object') {
    return scores;
  }
  return {};
};

const collectMatchFeedbackRecordCandidates = (responseData) => {
  const payload = responseData?.data !== undefined ? responseData.data : responseData;
  const candidates = [];
  const pushList = (list) => {
    if (Array.isArray(list)) list.forEach((r) => candidates.push(r));
  };
  pushList(Array.isArray(payload) ? payload : null);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    pushList(payload.feedbacks);
    pushList(payload.records);
    pushList(payload.data);
    pushList(payload.feedback);
  }
  if (candidates.length === 0 && payload && typeof payload === 'object' && !Array.isArray(payload)) {
    candidates.push(payload);
  }
  return candidates;
};

/**
 * Finds feedback rows saved for one event-team + calendar day.
 * Handles both “one Mongo document per day” and “nested entries with day on each row”.
 */
const extractFeedbackForTeamAndDay = (responseData, eventTeamId, dayNumber) => {
  const targetEventTeamId = String(eventTeamId || '').trim();
  const targetDay = Number(dayNumber);
  if (!targetEventTeamId || !Number.isFinite(targetDay) || targetDay < 1) {
    return { entries: [], overallSource: null };
  }

  const candidates = collectMatchFeedbackRecordCandidates(responseData);
  const recordMatchesTeam = (record) => {
    const rid = String(record?.eventTeamId?._id || record?.eventTeamId?.id || record?.eventTeamId || '').trim();
    return !rid || rid === targetEventTeamId;
  };

  const directRecord = candidates.find((record) => {
    if (!record || typeof record !== 'object' || !recordMatchesTeam(record)) return false;
    const recordDay = Number(record?.feedbackDay ?? record?.matchDay ?? record?.dayNumber ?? '');
    return Number.isFinite(recordDay) && recordDay === targetDay;
  });

  if (directRecord) {
    return {
      entries: extractFeedbackEntries(directRecord),
      overallSource: directRecord,
    };
  }

  for (const record of candidates) {
    if (!record || typeof record !== 'object' || !recordMatchesTeam(record)) continue;
    const recordDayFallback = Number(record?.feedbackDay ?? record?.matchDay ?? record?.dayNumber ?? '');
    const nested = extractFeedbackEntries(record).filter((entry) => {
      const entryDay = Number(entry?.feedbackDay ?? entry?.matchDay ?? entry?.dayNumber ?? '');
      const effective = Number.isFinite(entryDay) ? entryDay : recordDayFallback;
      return Number.isFinite(effective) && effective === targetDay;
    });
    if (nested.length > 0) {
      return { entries: nested, overallSource: record };
    }
  }

  return { entries: [], overallSource: null };
};

const buildRowsFromFeedbackEntries = (
  existingEntries,
  activeParameters,
  teamMembers,
  jobTypeNameById
) =>
  existingEntries.map((entry, index) => {
    const roleLabel = resolveEntryRoleLabel(entry, `Member ${index + 1}`);
    const sourceScores = normalizeScoresShape(
      entry?.scores || entry?.ratings || entry?.parameterScores
    );
    const abandonedEntry = isAbandonedFeedbackEntry(entry);
    const nextScores = activeParameters.reduce((acc, parameterName) => {
      acc[parameterName] = abandonedEntry
        ? ABANDONED_FEEDBACK_MARKER
        : resolveScoreValue(sourceScores, parameterName, null);
      return acc;
    }, {});

    const normalizedEntryEmployeeId = resolveEntryEmployeeId(entry);
    const roleMatchedMembers = teamMembers.filter((member) => {
      const memberRole = resolveTeamMemberRole(member);
      const memberRoleId = resolveTeamMemberRoleId(member);
      return (
        normalizeJobType(memberRole) === normalizeJobType(roleLabel) ||
        (memberRoleId && normalizeJobType(memberRoleId) === normalizeJobType(roleLabel))
      );
    });
    const sameRoleEntryIndex = existingEntries
      .slice(0, index)
      .filter(
        (prevEntry) =>
          normalizeJobType(resolveEntryRoleLabel(prevEntry, '')) === normalizeJobType(roleLabel)
      ).length;
    const matchedMember = normalizedEntryEmployeeId
      ? teamMembers.find((member) => resolveTeamMemberId(member) === normalizedEntryEmployeeId)
      : roleMatchedMembers.length === 1
      ? roleMatchedMembers[0]
      : roleMatchedMembers[sameRoleEntryIndex] || roleMatchedMembers[0] || null;

    const baseRowId = toStableRowId(
      entry?._id || entry?.id || entry?.employeeId,
      `${roleLabel}-${index}`
    );

    return {
      id: `${baseRowId}-${index}`,
      jobType:
        jobTypeNameById[normalizeJobType(roleLabel)] ||
        roleLabel ||
        resolveTeamMemberRole(matchedMember) ||
        entry?.employeeId?.jobType?.name ||
        '-',
      employeeName:
        resolveEntryEmployeeName(entry) || resolveTeamMemberName(matchedMember) || '-',
      scores: nextScores,
      remarks: entry?.remarks || '',
    };
  });

const cloneRows = (rows = []) =>
  rows.map((row) => ({
    ...row,
    scores: { ...(row?.scores || {}) },
  }));

const EventFeedbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    jobTypeRows: ctxJobTypeRows,
    feedbackParameterRows: ctxFeedbackParameterRows,
    loading: referenceDataLoading,
  } = useReferenceData();
  const { id, matchId: routeMatchId } = useParams();
  const backPath = location.state?.from || null;
  const activeMatchId = location.state?.matchId
    ? String(location.state.matchId)
    : routeMatchId
    ? String(routeMatchId)
    : null;
  const isViewMode = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get('mode') === 'view';
  }, [location.search]);
  const appBasePath = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return user?.role === 'admin' ? '/admin' : '/employee';
    } catch (error) {
      return '/employee';
    }
  }, []);
  const currentUserName = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return (
        user?.name ||
        user?.fullName ||
        user?.userName ||
        user?.username ||
        user?.email ||
        ''
      );
    } catch (error) {
      return '';
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [eventTeam, setEventTeam] = useState(null);
  const isDaysMatch = useMemo(
    () => Boolean(activeMatchId) && isDaysMatchType(resolveMatchTypeForEventFeedback(eventTeam, location.state)),
    [activeMatchId, eventTeam, location.state]
  );
  const daysDuration = useMemo(() => {
    if (!isDaysMatch) return 1;
    return getDaysDuration(
      location.state?.startDate ||
        eventTeam?.matchStartDate ||
        eventTeam?.match?.startDate ||
        eventTeam?.startDate ||
        eventTeam?.date,
      location.state?.endDate ||
        eventTeam?.matchEndDate ||
        eventTeam?.match?.endDate ||
        eventTeam?.endDate
    );
  }, [
    isDaysMatch,
    location.state?.startDate,
    location.state?.endDate,
    eventTeam?.matchStartDate,
    eventTeam?.matchEndDate,
    eventTeam?.match?.startDate,
    eventTeam?.match?.endDate,
    eventTeam?.startDate,
    eventTeam?.endDate,
    eventTeam?.date,
  ]);
  const dayOptions = useMemo(
    () => (isDaysMatch ? Array.from({ length: daysDuration }, (_, index) => index + 1) : [1]),
    [isDaysMatch, daysDuration]
  );
  const [parameters, setParameters] = useState([]);
  const [rows, setRows] = useState([]);
  const [overallFeedback, setOverallFeedback] = useState('');
  const [evaluator, setEvaluator] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionLocked, setSubmissionLocked] = useState(false);
  const [submitWarningOpen, setSubmitWarningOpen] = useState(false);
  const [submitIntent, setSubmitIntent] = useState('feedback');
  const [selectedFeedbackDay, setSelectedFeedbackDay] = useState(1);
  const [submittedDays, setSubmittedDays] = useState([]);
  const [dayDrafts, setDayDrafts] = useState({});
  const [baseRowsTemplate, setBaseRowsTemplate] = useState([]);
  const [baseOverallFeedback, setBaseOverallFeedback] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [scheduleTick, setScheduleTick] = useState(0);
  const previousSelectedDayRef = useRef(1);
  const rolePermissionMatrixRef = useRef({});

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    if (!isDaysMatch) {
      setSelectedFeedbackDay(1);
      previousSelectedDayRef.current = 1;
      return;
    }
    if (!dayOptions.includes(selectedFeedbackDay)) {
      setSelectedFeedbackDay(dayOptions[0] || 1);
    }
  }, [isDaysMatch, dayOptions, selectedFeedbackDay]);

  useEffect(() => {
    const loadPageData = async () => {
      if (!id) {
        toast.error('Event team id is missing');
        navigate(backPath || `${appBasePath}/teams-events`);
        return;
      }

      let user = {};
      try {
        user = JSON.parse(sessionStorage.getItem('user') || '{}');
      } catch {
        user = {};
      }
      const role = user?.role;

      let matrix = {};
      try {
        const permResponse = await api.getPermissions();
        const permissionRows = Array.isArray(permResponse?.data)
          ? permResponse.data
          : Array.isArray(permResponse?.data?.permissions)
          ? permResponse.data.permissions
          : Array.isArray(permResponse?.permissions)
          ? permResponse.permissions
          : [];
        matrix = buildRoleFeatureMatrix(permissionRows);
      } catch {
        matrix = {};
      }
      rolePermissionMatrixRef.current = matrix;

      if (isViewMode) {
        const matchScopedView = Boolean(location.state?.matchId);
        // Match TL read: only "View TL Feedback" (Matches), independent of Teams "View Feedback" / Give TL.
        const canViewThisFeedback = matchScopedView
          ? canFeatureForRole(role, matrix, [PERMISSIONS.VIEW_MATCH_TL_FEEDBACK])
          : canFeatureForRole(role, matrix, [
              PERMISSIONS.VIEW_TEAM_EVENT_FEEDBACK,
              PERMISSIONS.VIEW_TEAM_FEEDBACK,
              'view_team_event_feedback',
            ]);
        if (!canViewThisFeedback) {
          toast.error(
            matchScopedView
              ? 'You do not have permission to view match TL feedback'
              : 'You do not have permission to view Team feedback'
          );
          navigate(backPath || (matchScopedView ? `${appBasePath}/matches` : `${appBasePath}/teams-events`));
          return;
        }
      }

      if (referenceDataLoading) {
        setLoading(true);
        return;
      }

      try {
        setLoading(true);
        const feedbackRequest = isViewMode
          ? location.state?.feedbackData
            ? Promise.resolve(location.state.feedbackData)
            : location.state?.matchId
            ? api.getEventFeedbackByMatchId(String(location.state.matchId)).catch((err) => {
                if (err?.status === 404) return null;
                throw err;
              })
            : api.getEventFeedbackByEventTeamId(id)
          : Promise.resolve(null);
        const submittedDaysRequest =
          !isViewMode && isDaysMatch && activeMatchId
            ? api.getEventFeedbackByMatchId(String(activeMatchId)).catch((err) => {
                if (err?.status === 404) return null;
                throw err;
              })
            : Promise.resolve(null);
        const matchParametersRequest = activeMatchId
          ? api.getMatchById(String(activeMatchId)).catch(() => null)
          : Promise.resolve(null);

        const [eventResponse, feedbackResponse, submittedDaysResponse, matchParametersResponse] =
          await Promise.all([
            api.getEventTeamById(id),
            feedbackRequest,
            submittedDaysRequest,
            matchParametersRequest,
          ]);

        const eventItem =
          eventResponse?.data?.data ||
          eventResponse?.data ||
          eventResponse ||
          {};
        let parameterRows = Array.isArray(ctxFeedbackParameterRows)
          ? ctxFeedbackParameterRows
          : [];
        const matchDoc =
          matchParametersResponse?.data ??
          matchParametersResponse ??
          null;
        const snapshotParams = Array.isArray(matchDoc?.feedbackParameterIds)
          ? matchDoc.feedbackParameterIds
          : [];
        if (snapshotParams.length > 0) {
          const snapshotNames = snapshotParams
            .map((fp) => {
              if (fp && typeof fp === 'object') {
                return String(
                  fp.name || fp.parameter || fp.title || fp.feedbackParameter || ''
                ).trim();
              }
              return '';
            })
            .filter(Boolean);
          if (snapshotNames.length > 0) {
            parameterRows = snapshotNames.map((name) => ({ name }));
          }
        }
        const jobTypeRows = Array.isArray(ctxJobTypeRows) ? ctxJobTypeRows : [];
        const jobTypeNameById = jobTypeRows.reduce((acc, item) => {
          const id = item?._id || item?.id || item?.value;
          const name = item?.name || item?.jobType || item?.title || '';
          if (id && name) acc[normalizeJobType(String(id))] = String(name).trim();
          return acc;
        }, {});

        const normalizedParameters = parameterRows
          .map((item) =>
            String(
              item?.name ||
                item?.parameter ||
                item?.title ||
                item?.feedbackParameter ||
                ''
            ).trim()
          )
          .filter(Boolean);

        const teamMembers = Array.isArray(eventItem?.teamMembers)
          ? eventItem.teamMembers
          : Array.isArray(eventItem?.members)
          ? eventItem.members
          : [];

        const uniqueRoles = Array.from(
          new Set(
            teamMembers
              .map((item) => {
                const memberRole = item?.role?.name || item?.role || '';
                return String(memberRole).trim();
              })
              .filter(Boolean)
              .filter((role) => normalizeJobType(role) !== normalizeJobType('Team Leader'))
          )
        );

        const existingFeedbackData = extractFeedbackPayload(feedbackResponse);
        const existingEntriesView = extractFeedbackEntries(existingFeedbackData);

        const eventTeamIdForLookup = String(eventItem?._id || eventItem?.id || id || '').trim();

        const savedEntriesForInference = [];
        if (!isViewMode && isDaysMatch && submittedDaysResponse && eventTeamIdForLookup) {
          dayOptions.forEach((dayNum) => {
            const { entries } = extractFeedbackForTeamAndDay(
              submittedDaysResponse,
              eventTeamIdForLookup,
              dayNum
            );
            entries.forEach((e) => savedEntriesForInference.push(e));
          });
        }

        const inferredFromView =
          isViewMode && existingEntriesView.length > 0
            ? Array.from(
                new Set(
                  existingEntriesView.flatMap((entry) =>
                    getParameterNamesFromScores(
                      normalizeScoresShape(entry?.scores || entry?.ratings || entry?.parameterScores)
                    )
                  )
                )
              )
            : [];

        const inferredFromSavedDays =
          savedEntriesForInference.length > 0
            ? Array.from(
                new Set(
                  savedEntriesForInference.flatMap((entry) =>
                    getParameterNamesFromScores(
                      normalizeScoresShape(entry?.scores || entry?.ratings || entry?.parameterScores)
                    )
                  )
                )
              )
            : [];

        const inferredParameters = [...new Set([...inferredFromView, ...inferredFromSavedDays])];
        const activeParameters =
          inferredParameters.length > 0 ? inferredParameters : normalizedParameters;

        const defaultRows = uniqueRoles.map((role, index) => {
          const assignedMember = teamMembers.find((member) => {
            const memberRole = member?.role?.name || member?.role || '';
            return normalizeJobType(memberRole) === normalizeJobType(role);
          });

          return {
            id: `${role}-${index}`,
            jobType: role,
            employeeName: resolveTeamMemberName(assignedMember) || '-',
            scores: activeParameters.reduce((acc, parameterName) => {
              acc[parameterName] = 5;
              return acc;
            }, {}),
            remarks: '',
          };
        });

        const populatedRows =
          isViewMode && existingEntriesView.length > 0
            ? buildRowsFromFeedbackEntries(
                existingEntriesView,
                activeParameters,
                teamMembers,
                jobTypeNameById
              )
            : defaultRows;

        const initialOverallFeedback = isViewMode ? existingFeedbackData?.overallFeedback || '' : '';
        const initialDay = 1;

        setEventTeam(eventItem);
        setParameters(activeParameters);

        const freshDefaultTemplate = cloneRows(defaultRows);

        if (isDaysMatch && !isViewMode && submittedDaysResponse && eventTeamIdForLookup) {
          const initialDrafts = {};
          dayOptions.forEach((dayNum) => {
            const { entries, overallSource } = extractFeedbackForTeamAndDay(
              submittedDaysResponse,
              eventTeamIdForLookup,
              dayNum
            );
            if (entries.length > 0) {
              initialDrafts[dayNum] = {
                rows: cloneRows(
                  buildRowsFromFeedbackEntries(
                    entries,
                    activeParameters,
                    teamMembers,
                    jobTypeNameById
                  )
                ),
                overallFeedback:
                  overallSource?.overallFeedback ||
                  overallSource?.overall_feedback ||
                  overallSource?.summary ||
                  '',
              };
            } else {
              initialDrafts[dayNum] = {
                rows: cloneRows(defaultRows),
                overallFeedback: '',
              };
            }
          });
          setBaseRowsTemplate(freshDefaultTemplate);
          setBaseOverallFeedback('');
          setDayDrafts(initialDrafts);
          previousSelectedDayRef.current = initialDay;
          setRows(cloneRows(initialDrafts[initialDay]?.rows || defaultRows));
          setOverallFeedback(initialDrafts[initialDay]?.overallFeedback || '');
        } else {
          setBaseRowsTemplate(isDaysMatch && !isViewMode ? freshDefaultTemplate : cloneRows(populatedRows));
          setBaseOverallFeedback(initialOverallFeedback);
          if (isDaysMatch && !isViewMode) {
            setDayDrafts({
              [initialDay]: {
                rows: cloneRows(populatedRows),
                overallFeedback: initialOverallFeedback,
              },
            });
          } else {
            setDayDrafts({});
          }
          previousSelectedDayRef.current = initialDay;
          setRows(cloneRows(populatedRows));
          if (!isViewMode) {
            setOverallFeedback(initialOverallFeedback);
          }
        }
        if (!isViewMode && isDaysMatch && activeMatchId) {
          const submitted = extractSubmittedDaysForEventTeam(
            submittedDaysResponse,
            eventItem?._id || eventItem?.id || id
          );
          setSubmittedDays(submitted);
        } else {
          setSubmittedDays([]);
        }
        if (isViewMode) {
          setOverallFeedback(initialOverallFeedback);
          setEvaluator(
            resolveEvaluatorName(existingFeedbackData?.evaluator) ||
              resolveEvaluatorName(existingFeedbackData?.createdBy)
          );
          setSubmissionLocked(true);
        } else {
          setEvaluator(currentUserName);
        }
      } catch (error) {
        console.error('Error loading feedback page data:', error);
        toast.error(error.message || 'Unable to load feedback form');
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, [
    id,
    isViewMode,
    location.state,
    navigate,
    currentUserName,
    appBasePath,
    isDaysMatch,
    activeMatchId,
    backPath,
    dayOptions,
    referenceDataLoading,
    ctxJobTypeRows,
    ctxFeedbackParameterRows,
  ]);

  useEffect(() => {
    if (!isDaysMatch || isViewMode) return;
    const previousDay = previousSelectedDayRef.current;
    if (previousDay === selectedFeedbackDay) return;

    setDayDrafts((prev) => {
      const next = {
        ...prev,
        [previousDay]: {
          rows: cloneRows(rows),
          overallFeedback,
        },
      };

      if (!next[selectedFeedbackDay]) {
        next[selectedFeedbackDay] = {
          rows: cloneRows(baseRowsTemplate),
          overallFeedback: baseOverallFeedback,
        };
      }

      return next;
    });
    previousSelectedDayRef.current = selectedFeedbackDay;
  }, [
    selectedFeedbackDay,
    isDaysMatch,
    isViewMode,
    rows,
    overallFeedback,
    baseRowsTemplate,
    baseOverallFeedback,
  ]);

  useEffect(() => {
    if (!isDaysMatch || isViewMode) return;
    const draft = dayDrafts[selectedFeedbackDay];
    if (!draft) return;
    setRows(cloneRows(draft.rows));
    setOverallFeedback(draft.overallFeedback || '');
    setCurrentPage(1);
  }, [selectedFeedbackDay, dayDrafts, isDaysMatch, isViewMode]);

  const formatDisplayDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDisplayTime = (value) => {
    if (!value) return '';
    const parsed = new Date(`1970-01-01T${value}`);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDisplayDateTime = (dateValue, timeValue) => {
    const dateText = formatDisplayDate(dateValue);
    const timeText = formatDisplayTime(timeValue);
    if (!dateText && !timeText) return '';
    if (!dateText) return timeText;
    if (!timeText) return dateText;
    return `${dateText} ${timeText}`;
  };

  const matchNameLabel = resolveLabelText(
    location.state?.matchName ||
      eventTeam?.matchName ||
      eventTeam?.match?.matchName ||
      eventTeam?.match?.name ||
      eventTeam?.match ||
      eventTeam?.eventName ||
      eventTeam?.event
  );
  const venueLabel = resolveLabelText(
    location.state?.matchLocation ||
      location.state?.venue ||
      location.state?.match?.matchLocation ||
      location.state?.match?.venue ||
      location.state?.matchData?.matchLocation ||
      location.state?.matchData?.venue ||
      eventTeam?.matchId?.matchLocation ||
      eventTeam?.matchId?.venue ||
      eventTeam?.matchLocation ||
      eventTeam?.venue ||
      eventTeam?.match?.matchLocation ||
      eventTeam?.match?.venue ||
      eventTeam?.event?.matchLocation ||
      eventTeam?.event?.venue
  );
  const teamNameLabel = resolveLabelText(
    location.state?.teamName ||
      eventTeam?.teamName ||
      eventTeam?.eventName ||
      eventTeam?.team?.teamName ||
      eventTeam?.team?.name
  );
  const matchStartDateTime = formatDisplayDateTime(
    location.state?.startDate ||
      eventTeam?.matchStartDate ||
      eventTeam?.match?.startDate ||
      eventTeam?.startDate ||
      eventTeam?.date,
    location.state?.startTime ||
      eventTeam?.matchStartTime ||
      eventTeam?.match?.startTime ||
      eventTeam?.startTime
  );
  const matchEndDateTime = formatDisplayDateTime(
    location.state?.endDate ||
      eventTeam?.matchEndDate ||
      eventTeam?.match?.endDate ||
      eventTeam?.endDate,
    location.state?.endTime ||
      eventTeam?.matchEndTime ||
      eventTeam?.match?.endTime ||
      eventTeam?.endTime
  );
  const matchInfoItems = [
    { label: 'Match', value: matchNameLabel || '-' },
    { label: 'Team', value: teamNameLabel || '-' },
    { label: 'Venue', value: venueLabel || '-' },
    { label: 'Start Date & Time', value: matchStartDateTime || '-' },
    { label: 'End Date & Time', value: matchEndDateTime || '-' },
  ];

  const scheduleStartDate =
    location.state?.startDate ||
    eventTeam?.matchStartDate ||
    eventTeam?.match?.startDate ||
    eventTeam?.startDate ||
    eventTeam?.date;
  const scheduleEndDate =
    location.state?.endDate ||
    eventTeam?.matchEndDate ||
    eventTeam?.match?.endDate ||
    eventTeam?.endDate;
  const scheduleEndTime =
    location.state?.endTime ||
    eventTeam?.matchEndTime ||
    eventTeam?.match?.endTime ||
    eventTeam?.endTime;
  const scheduleStartTime =
    location.state?.startTime ||
    eventTeam?.matchStartTime ||
    eventTeam?.match?.startTime ||
    eventTeam?.startTime;

  void scheduleTick;

  const tlFeedbackScheduleOpen =
    isViewMode || !activeMatchId
      ? true
      : isTlFeedbackWindowOpen({
          isDaysMatch,
          startDate: scheduleStartDate,
          startTime: scheduleStartTime,
          dayNumber: selectedFeedbackDay,
          now: new Date(),
        });

  const updateScore = (rowId, parameterName, nextValue) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              scores: {
                ...row.scores,
                [parameterName]:
                  nextValue === ABANDONED_FEEDBACK_MARKER ||
                  String(nextValue).trim().toUpperCase() === 'N/A'
                    ? ABANDONED_FEEDBACK_MARKER
                    : normalizeRatingValue(nextValue, 1),
              },
            }
          : row
      )
    );
  };

  const updateRemarks = (rowId, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, remarks: value } : row))
    );
  };

  const rowAverages = useMemo(
    () =>
      rows.reduce((acc, row) => {
        const rawVals = parameters.map((parameterName) => row.scores?.[parameterName]);
        if (
          rawVals.length > 0 &&
          rawVals.every(
            (value) =>
              value === ABANDONED_FEEDBACK_MARKER || isAbandonMarkerScoreValue(value)
          )
        ) {
          acc[row.id] = null;
          return acc;
        }
        const validScores = rawVals
          .filter((value) => Number.isFinite(Number(value)))
          .map((value) => Number(value));
        const total = validScores.reduce((sum, value) => sum + value, 0);
        const average = validScores.length > 0 ? total / validScores.length : 0;
        acc[row.id] = average;
        return acc;
      }, {}),
    [rows, parameters]
  );

  const quickSummary = useMemo(() => {
    if (rows.length === 0) return 0;
    const numericAvgs = rows
      .map((row) => rowAverages[row.id])
      .filter((v) => v != null && typeof v === 'number' && Number.isFinite(v));
    if (numericAvgs.length === 0) return null;
    return numericAvgs.reduce((sum, v) => sum + v, 0) / numericAvgs.length;
  }, [rows, rowAverages]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / itemsPerPage)),
    [rows.length, itemsPerPage]
  );

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return rows.slice(startIndex, startIndex + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (isViewMode || !activeMatchId) return undefined;
    const id = window.setInterval(() => setScheduleTick((x) => x + 1), 30000);
    return () => window.clearInterval(id);
  }, [isViewMode, activeMatchId]);

  const prepareFeedbackPayload = useCallback(() => {
    const eventTeamId = eventTeam?._id || eventTeam?.id || id;
    if (!eventTeamId) {
      return { ok: false, error: 'Event team id is missing' };
    }

    const sourceMembers = Array.isArray(eventTeam?.teamMembers)
      ? eventTeam.teamMembers
      : Array.isArray(eventTeam?.members)
      ? eventTeam.members
      : [];

    const feedback = rows
      .map((row) => {
        const matchedMember = sourceMembers.find((member) => {
          const memberRole = member?.role?.name || member?.role || '';
          return normalizeJobType(memberRole) === normalizeJobType(row.jobType);
        });
        const employeeId =
          matchedMember?.member?._id ||
          matchedMember?.member?.id ||
          (typeof matchedMember?.member === 'string' ? matchedMember.member : null) ||
          matchedMember?.employeeId ||
          matchedMember?.userId ||
          matchedMember?.memberId ||
          matchedMember?.employee?._id ||
          matchedMember?.employee?.id ||
          matchedMember?.id ||
          matchedMember?._id;

        if (!employeeId) return null;

        const scores = Object.entries(row.scores || {}).reduce((acc, [parameterName, value]) => {
          const normalizedValue = Math.max(1, Math.min(5, Number(value) || 1));
          const labelKey = String(parameterName || '').trim();
          // Send only the exact parameter name — backend validates against stored parameter names
          if (labelKey) acc[labelKey] = normalizedValue;
          return acc;
        }, {});

        return {
          employeeId: String(employeeId),
          role: row.jobType,
          scores,
          remarks: row.remarks || '',
        };
      })
      .filter(Boolean);

    if (!feedback.length) {
      return {
        ok: false,
        error: 'No valid team members available for feedback submission',
      };
    }

    return { ok: true, eventTeamId: String(eventTeamId), feedback };
  }, [eventTeam, rows, id]);

  const prepareAbandonedFeedbackPayload = useCallback(() => {
    const eventTeamId = eventTeam?._id || eventTeam?.id || id;
    if (!eventTeamId) {
      return { ok: false, error: 'Event team id is missing' };
    }

    const sourceMembers = Array.isArray(eventTeam?.teamMembers)
      ? eventTeam.teamMembers
      : Array.isArray(eventTeam?.members)
      ? eventTeam.members
      : [];

    const feedback = rows
      .map((row) => {
        const matchedMember = sourceMembers.find((member) => {
          const memberRole = member?.role?.name || member?.role || '';
          return normalizeJobType(memberRole) === normalizeJobType(row.jobType);
        });
        const employeeId =
          matchedMember?.member?._id ||
          matchedMember?.member?.id ||
          (typeof matchedMember?.member === 'string' ? matchedMember.member : null) ||
          matchedMember?.employeeId ||
          matchedMember?.userId ||
          matchedMember?.memberId ||
          matchedMember?.employee?._id ||
          matchedMember?.employee?.id ||
          matchedMember?.id ||
          matchedMember?._id;

        if (!employeeId) return null;

        const scores = parameters.reduce((acc, parameterName) => {
          const labelKey = String(parameterName || '').trim();
          if (labelKey) acc[labelKey] = ABANDONED_FEEDBACK_MARKER;
          return acc;
        }, {});

        return {
          employeeId: String(employeeId),
          role: row.jobType,
          scores,
          remarks: ABANDONED_FEEDBACK_MARKER,
        };
      })
      .filter(Boolean);

    if (!feedback.length) {
      return {
        ok: false,
        error: 'No valid team members available for abandoned submission',
      };
    }

    return { ok: true, eventTeamId: String(eventTeamId), feedback };
  }, [eventTeam, rows, id, parameters]);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (isViewMode || submissionLocked) return;
    if (!isViewMode && activeMatchId && !tlFeedbackScheduleOpen) {
      toast.error(
        isDaysMatch
          ? `Feedback opens when Day ${selectedFeedbackDay} starts (scheduled start: ${formatScheduledStartLabel(true, scheduleStartDate, scheduleStartTime, selectedFeedbackDay) || '—'}).`
          : `Feedback opens when the match starts (scheduled start: ${formatScheduledStartLabel(false, scheduleStartDate, scheduleStartTime, 1) || '—'}).`
      );
      return;
    }
    if (isDaysMatch && submittedDays.includes(selectedFeedbackDay)) {
      toast.error(`Feedback already submitted for Day ${selectedFeedbackDay}`);
      return;
    }

    const prepared = prepareFeedbackPayload();
    if (!prepared.ok) {
      toast.error(prepared.error);
      return;
    }

    setSubmitIntent('feedback');
    setSubmitWarningOpen(true);
  };

  const handleAbandonedSubmit = () => {
    if (isViewMode || submissionLocked || submitting) return;
    if (!isViewMode && activeMatchId && !tlFeedbackScheduleOpen) {
      toast.error(
        isDaysMatch
          ? `You can mark abandoned only after Day ${selectedFeedbackDay} has started.`
          : 'You can mark abandoned only after the scheduled match start.'
      );
      return;
    }
    if (isDaysMatch && submittedDays.includes(selectedFeedbackDay)) {
      toast.error(`Day ${selectedFeedbackDay} is already submitted`);
      return;
    }
    setSubmitIntent('abandoned');
    setSubmitWarningOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const isAbandonedIntent = submitIntent === 'abandoned';
    const prepared = isAbandonedIntent
      ? prepareAbandonedFeedbackPayload()
      : prepareFeedbackPayload();
    if (!prepared.ok || !prepared.eventTeamId) {
      toast.error(prepared.error || 'Event team id is missing');
      setSubmitWarningOpen(false);
      return;
    }

    if (!isViewMode && activeMatchId && !tlFeedbackScheduleOpen) {
      toast.error('This match day has not reached its scheduled end yet.');
      setSubmitWarningOpen(false);
      return;
    }

    const matchId = activeMatchId;
    if (matchId) {
      let user = {};
      try {
        user = JSON.parse(sessionStorage.getItem('user') || '{}');
      } catch {
        user = {};
      }
      const role = user?.role;
      if (
        !canFeatureForRole(role, rolePermissionMatrixRef.current, [PERMISSIONS.GIVE_MATCH_TL_FEEDBACK])
      ) {
        toast.error('You do not have permission to submit TL feedback for this match');
        setSubmitWarningOpen(false);
        return;
      }
    }

    try {
      setSubmitting(true);

      const payload = {
        eventTeamId: prepared.eventTeamId,
        feedback: prepared.feedback,
        overallFeedback,
        evaluator: evaluator || currentUserName,
      };
      if (isAbandonedIntent) {
        payload.overallFeedback =
          overallFeedback?.trim() || 'Match abandoned';
        payload.abandoned = true;
        payload.isAbandoned = true;
        payload.matchStatus = 'abandoned';
        payload.feedback = (payload.feedback || []).map((entry) => ({
          ...entry,
          scores: parameters.reduce((acc, parameterName) => {
            const key = String(parameterName || '').trim();
            if (key) acc[key] = ABANDONED_FEEDBACK_MARKER;
            return acc;
          }, {}),
          remarks: ABANDONED_FEEDBACK_MARKER,
        }));
      }
      if (isDaysMatch) {
        payload.feedbackDay = selectedFeedbackDay;
        payload.matchDay = selectedFeedbackDay;
        payload.dayNumber = selectedFeedbackDay;
      }

      const response = matchId
        ? await api.submitMatchFeedback({ ...payload, matchId })
        : await api.submitEventFeedback(payload);

      const nextSubmittedDays = isDaysMatch
        ? (() => {
            const merged = new Set(submittedDays);
            merged.add(selectedFeedbackDay);
            return Array.from(merged).sort((a, b) => a - b);
          })()
        : [];

      const finishAfterSuccessfulSubmit = () => {
        setSubmitWarningOpen(false);

        if (!isDaysMatch) {
          setSubmissionLocked(true);
          navigate(backPath || `${appBasePath}/teams-events`);
          return;
        }

        setSubmittedDays(nextSubmittedDays);
        const nextPendingDay = dayOptions.find(
          (dayNumber) => !nextSubmittedDays.includes(dayNumber)
        );

        if (nextPendingDay) {
          setSelectedFeedbackDay(nextPendingDay);
          return;
        }

        setSubmissionLocked(true);
        navigate(backPath || `${appBasePath}/teams-events`);
      };

      if (response?.status === 201) {
        toast.success(
          isAbandonedIntent
            ? isDaysMatch
              ? `Day ${selectedFeedbackDay} marked as abandoned`
              : 'Match marked as abandoned'
            : isDaysMatch
            ? `Day ${selectedFeedbackDay} feedback submitted successfully`
            : 'Feedback submitted successfully'
        );
        finishAfterSuccessfulSubmit();
        return;
      }

      toast.success(
        isAbandonedIntent
          ? isDaysMatch
            ? `Day ${selectedFeedbackDay} marked as abandoned`
            : 'Match marked as abandoned'
          : isDaysMatch
          ? `Day ${selectedFeedbackDay} feedback submitted successfully`
          : 'Feedback submitted successfully'
      );
      finishAfterSuccessfulSubmit();
    } catch (error) {
      if (error?.status === 409) {
        toast.error(
          isDaysMatch
            ? `Feedback already submitted for Day ${selectedFeedbackDay}`
            : 'Feedback already submitted'
        );
        if (isDaysMatch) {
          setSubmittedDays((prev) =>
            prev.includes(selectedFeedbackDay)
              ? prev
              : [...prev, selectedFeedbackDay].sort((a, b) => a - b)
          );
        } else {
          setSubmissionLocked(true);
        }
        setSubmitWarningOpen(false);
        return;
      }
      toast.error(error.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const isScheduleBlocked =
    !isViewMode && Boolean(activeMatchId) && !tlFeedbackScheduleOpen;

  const isSelectedDayAlreadySubmitted =
    isDaysMatch && submittedDays.includes(selectedFeedbackDay);
  const isSelectedDayReadOnly =
    isViewMode ||
    submissionLocked ||
    isSelectedDayAlreadySubmitted ||
    isScheduleBlocked;

  const getDayPillStyle = (dayNumber) => {
    const isSubmitted = submittedDays.includes(dayNumber);
    const isSelected = selectedFeedbackDay === dayNumber;

    if (isSubmitted && isSelected) {
      return {
        background: '#16a34a',
        border: '1px solid #16a34a',
        color: 'white',
      };
    }

    if (isSubmitted) {
      return {
        background: '#dcfce7',
        border: '1px solid #22c55e',
        color: '#166534',
      };
    }

    return {
      background: isSelected ? '#ef4444' : '#fee2e2',
      border: isSelected ? '1px solid #ef4444' : '1px solid #f87171',
      color: isSelected ? 'white' : '#b91c1c',
    };
  };

  return (
    <div style={{ padding: '16px' }}>
      <ConfirmActionModal
        isOpen={submitWarningOpen}
        onClose={() => {
          if (!submitting) setSubmitWarningOpen(false);
        }}
        onConfirm={handleConfirmSubmit}
        title={submitIntent === 'abandoned' ? 'Mark match as abandoned?' : 'Submit feedback?'}
        message={
          submitIntent === 'abandoned'
            ? 'This will mark the selected match/day as abandoned and no employee feedback will be saved. This action is final and cannot be changed.'
            : 'Feedback for this event can only be submitted once. After you submit, you will not be able to change or send it again. Please confirm that your ratings and remarks are final.'
        }
        confirmText={submitIntent === 'abandoned' ? 'Yes, mark abandoned' : 'Yes, submit once'}
        cancelText="Go back and review"
        confirmColor={submitIntent === 'abandoned' ? '#dc2626' : '#2563eb'}
        loading={submitting}
      />
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div>
            {/* <div style={{ color: '#2563eb', fontSize: '12px', fontWeight: 700 }}>Feed Back</div> */}
            <h2 style={{ margin: '4px 0', color: '#101828' }}>Team Feedback</h2>
            {/* <p style={{ margin: 0, color: '#667085', fontSize: '13px' }}>
              Submit evaluator feedback for each job type in selected event.
            </p> */}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => navigate(backPath || `${appBasePath}/teams-events`)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '34px',
                padding: '0 14px',
                border: '1px solid #d0d5dd',
                background: '#ffffff',
                color: '#1e293b',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
              Back
            </button>
            {/* <button
              type="submit"
              form="event-feedback-form"
              disabled={isViewMode || submitting || loading || submissionLocked}
              style={{ padding: '9px 14px', border: 'none', background: '#2563eb', color: 'white', borderRadius: '10px', cursor: isViewMode || submitting || loading || submissionLocked ? 'not-allowed' : 'pointer', fontSize: '13px' }}
            >
              {isViewMode
                ? 'View Only'
                : submissionLocked
                ? 'Submitted'
                : submitting
                ? 'Submitting...'
                : 'Submit Feedback'}
            </button> */}
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: '14px', color: '#98a2b3', fontSize: '13px' }}>Loading feedback form...</div>
        ) : (
          <form id="event-feedback-form" onSubmit={handleFormSubmit} style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
            <div style={{ padding: '9px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', columnGap: '12px', rowGap: '0px', alignItems: 'start' }}>
                {matchInfoItems.map((item) => (
                  <div key={item.label}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', fontWeight: 500 }}>{item.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
            {isScheduleBlocked && (
              <div
                role="status"
                style={{
                  marginTop: '6px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #fcd34d',
                  background: '#fffbeb',
                  color: '#92400e',
                  fontSize: '12px',
                  lineHeight: 1.45,
                }}
              >
                {isDaysMatch
                  ? `TL feedback for Day ${selectedFeedbackDay} opens when that day starts (${formatScheduledStartLabel(true, scheduleStartDate, scheduleStartTime, selectedFeedbackDay) || '—'}).`
                  : `TL feedback opens when the match starts (${formatScheduledStartLabel(false, scheduleStartDate, scheduleStartTime, 1) || '—'}).`}
              </div>
            )}
            <div>
              {isDaysMatch && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#667085', fontWeight: 700, marginBottom: '6px' }}>
                    MATCH DAY
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
                    {dayOptions.map((dayNumber) => {
                      const pillStyle = getDayPillStyle(dayNumber);
                      return (
                        <button
                          key={dayNumber}
                          type="button"
                          onClick={() => setSelectedFeedbackDay(dayNumber)}
                          disabled={isViewMode || submissionLocked}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            cursor: isViewMode || submissionLocked ? 'not-allowed' : 'pointer',
                            opacity: isViewMode || submissionLocked ? 0.65 : 1,
                            ...pillStyle,
                          }}
                        >
                          {formatMatchDayPillLabel(dayNumber, scheduleStartDate)}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '2px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475467' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '999px', background: '#dcfce7', border: '1px solid #22c55e' }} />
                      Submitted
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475467' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '999px', background: '#fee2e2', border: '1px solid #f87171' }} />
                      Pending
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
              <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: `${520 + Math.max(parameters.length, 1) * 110}px`,
                  borderCollapse: 'collapse',
                }}
              >
                <thead style={FEEDBACK_TABLE_HEAD_GRADIENT}>
                  <tr>
                    <th
                      style={{
                        width: '52px',
                        minWidth: '52px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: '#f8fafc',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      S.No
                    </th>
                    <th
                      style={{
                        minWidth: '188px',
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: '#f8fafc',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Job Type
                    </th>
                    <th
                      style={{
                        minWidth: '136px',
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: '#f8fafc',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                    Member
                    </th>
                    {parameters.map((parameterName) => (
                      <th
                        key={parameterName}
                        style={{
                          minWidth: '110px',
                          padding: '12px 14px',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: '#f8fafc',
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        {formatParameterHeader(parameterName)}
                      </th>
                    ))}
                    <th
                      style={{
                        minWidth: '84px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: '#f8fafc',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Average
                    </th>
                    <th
                      style={{
                        minWidth: '120px',
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: '#f8fafc',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={parameters.length + 6} style={{ padding: '12px', textAlign: 'center', color: '#98a2b3', fontSize: '12px' }}>
                        No job types found in this event team.
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, index) => (
                      <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#64748b', textAlign: 'center', fontWeight: 600 }}>
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td
                          style={{
                            padding: '12px 14px',
                            fontSize: '13px',
                            color: '#334155',
                            fontWeight: 600,
                            verticalAlign: 'middle',
                            whiteSpace: 'nowrap',
                          }}
                          title={row.jobType}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                            {(() => {
                              const RoleIcon = getRoleIconComponent(row.jobType);
                              return <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />;
                            })()}
                            <span style={{ whiteSpace: 'nowrap' }}>{row.jobType}</span>
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', wordBreak: 'break-word' }}>
                            <FiUser size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {row.employeeName || '-'}
                          </span>
                        </td>
                        {parameters.map((parameterName) => (
                          <td key={`${row.id}-${parameterName}`} style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                            {isViewMode ? (
                              (() => {
                                const scoreValue = formatViewScoreValue(row.scores?.[parameterName]);
                                const isAbandonedScore = scoreValue === ABANDONED_FEEDBACK_MARKER;
                                return (
                              <div
                                style={{
                                  width: '100%',
                                  maxWidth: '58px',
                                  minHeight: '28px',
                                  padding: '4px 6px',
                                  margin: '0 auto',
                                  border: '1px solid #d0d5dd',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  background: isAbandonedScore ? '#fff1f2' : '#f8fafc',
                                  color: scoreValue === '-' ? '#98a2b3' : isAbandonedScore ? '#b42318' : '#111827',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: isAbandonedScore ? 700 : 500,
                                }}
                              >
                                {scoreValue}
                              </div>
                                );
                              })()
                            ) : (
                              <select
                                value={(() => {
                                  const s = row.scores?.[parameterName];
                                  if (s === ABANDONED_FEEDBACK_MARKER || isAbandonMarkerScoreValue(s)) {
                                    return ABANDONED_FEEDBACK_MARKER;
                                  }
                                  return String(normalizeRatingValue(s, 1));
                                })()}
                                onChange={(e) => updateScore(row.id, parameterName, e.target.value)}
                                disabled={isSelectedDayReadOnly}
                                style={{
                                  width: '58px',
                                  maxWidth: '58px',
                                  padding: '4px 6px',
                                  border: '1px solid #d0d5dd',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  verticalAlign: 'middle',
                                }}
                              >
                                <option value={ABANDONED_FEEDBACK_MARKER}>N/A</option>
                                {[1, 2, 3, 4, 5].map((score) => (
                                  <option key={score} value={String(score)}>
                                    {score}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        ))}
                        <td style={{ padding: '12px 14px', fontSize: '12px', color: '#5b21b6', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          {rowAverages[row.id] == null ? 'N/A' : rowAverages[row.id].toFixed(1)}
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <input
                            value={row.remarks}
                            onChange={(e) => updateRemarks(row.id, e.target.value)}
                            disabled={isSelectedDayReadOnly}
                            placeholder="Remark"
                            style={{ width: '100%', padding: '5px 6px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '12px' }}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={rows.length}
              itemsPerPage={itemsPerPage}
              showInfo={false}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', color: '#667085', fontWeight: 700, marginBottom: '6px' }}>OVERALL FEEDBACK</label>
                <textarea
                  value={overallFeedback}
                  onChange={(e) => setOverallFeedback(e.target.value)}
                  disabled={isSelectedDayReadOnly}
                  rows={4}
                  placeholder="Write overall feedback..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #d0d5dd', borderRadius: '12px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ border: '1px dashed #d0d5dd', borderRadius: '12px', padding: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>QUICK SUMMARY</div>
                    <div style={{ marginTop: '4px', fontSize: '38px', lineHeight: 1, fontWeight: 700, color: '#101828' }}>
                      {quickSummary == null ? 'N/A' : quickSummary.toFixed(1)}
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#667085' }}>
                        Average 
                      </div>
                      {(evaluator || 'Logged-in user') ? (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>EVALUATOR</div>
                          <div style={{ marginTop: '2px', fontSize: '13px', color: '#111827', fontWeight: 600 }}>{evaluator || 'Logged-in user'}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!isViewMode && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleAbandonedSubmit}
                    disabled={submitting || submissionLocked || isSelectedDayAlreadySubmitted}
                    style={{
                      padding: '8px 16px',
                      border: (submissionLocked || isSelectedDayAlreadySubmitted) ? '1px solid #d0d5dd' : '1px solid #fecaca',
                      background: (submissionLocked || isSelectedDayAlreadySubmitted) ? '#f2f4f7' : '#fff1f2',
                      color: (submissionLocked || isSelectedDayAlreadySubmitted) ? '#667085' : '#b91c1c',
                      borderRadius: '8px',
                      cursor: submitting || submissionLocked || isSelectedDayAlreadySubmitted ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(isSelectedDayAlreadySubmitted || submissionLocked)
                      ? 'Abandoned'
                      : submitting
                      ? 'Submitting...'
                      : 'Submit as Abandoned'}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || submissionLocked || isSelectedDayAlreadySubmitted}
                    style={{
                      padding: '8px 16px',
                      border: (submissionLocked || isSelectedDayAlreadySubmitted) ? '1px solid #d0d5dd' : 'none',
                      background: (submissionLocked || isSelectedDayAlreadySubmitted)
                        ? '#f2f4f7'
                        : 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                      color: (submissionLocked || isSelectedDayAlreadySubmitted) ? '#667085' : 'white',
                      borderRadius: '8px',
                      cursor: submitting || submissionLocked || isSelectedDayAlreadySubmitted ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(isSelectedDayAlreadySubmitted || submissionLocked)
                      ? 'Submitted'
                      : submitting
                      ? 'Submitting...'
                      : 'Submit'}
                  </button>
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EventFeedbackPage;
